/**
 * Unified per-driver message history + authoring pane.
 *
 * Reached from the Tasks active context, the Live Feed, and the drivers channel.
 * Messages and telematics-style activity events (empty/loaded calls, arrivals,
 * departures) are interleaved into a single chronological timeline. Each message
 * carries the product's affordances — a listen button and, for dispatch
 * messages, delivery receipts (sent / seen / heard) — and the pane has an inline
 * composer so a dispatcher can reply in place. All data is mocked/demo.
 */

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HearingIcon from "@mui/icons-material/Hearing";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";

import { type FeedMessage } from "./boards";
import { accent, text, zinc } from "./colors";
import { ActivityEventMarker } from "./components/ActivityTimeline";
import { MessageHistoryComposer } from "./components/MessageHistoryComposer";
import { Chip } from "./components/primitives";
import { hash } from "./hash";
import type { DriverActivityEvent } from "./types";

/** Receipt progression for a dispatch (outbound) message. */
type ReceiptStatus = "sent" | "seen" | "heard";

type TimelineEntry =
  | { kind: "message"; id: string; sortMs: number; message: FeedMessage }
  | { kind: "activity"; id: string; sortMs: number; event: DriverActivityEvent };

/**
 * Merge messages and activity events into one chronological stream. Activity
 * timestamps are wall-clock "HH:MM" with no date, so anchor them to the day of
 * the message history; messages carry full ISO timestamps.
 */
function buildTimeline(messages: FeedMessage[], activity: DriverActivityEvent[]): TimelineEntry[] {
  const newestMs = messages.reduce((max, m) => Math.max(max, new Date(m.createdAt).getTime()), Date.now());
  const activityMs = (timestamp: string): number => {
    const [hours, minutes] = timestamp.split(":").map(Number);
    const anchored = new Date(newestMs);
    anchored.setHours(hours, minutes || 0, 0, 0);
    // A clock time later than the newest message must belong to the previous day.
    if (anchored.getTime() > newestMs) anchored.setDate(anchored.getDate() - 1);
    return anchored.getTime();
  };
  return [
    ...messages.map(
      (message): TimelineEntry => ({
        kind: "message",
        id: message.itemId,
        sortMs: new Date(message.createdAt).getTime(),
        message,
      }),
    ),
    ...activity.map(
      (event): TimelineEntry => ({ kind: "activity", id: event.id, sortMs: activityMs(event.timestamp), event }),
    ),
  ].sort((a, b) => a.sortMs - b.sortMs);
}

/** Deterministic demo playback length, e.g. 0:07, for a message's listen button. */
function listenDuration(itemId: string): string {
  const seconds = 3 + (hash(`listen::${itemId}`) % 24);
  return `0:${String(seconds).padStart(2, "0")}`;
}

function ListenButton({ itemId }: { itemId: string }) {
  const [playing, setPlaying] = useState(false);
  return (
    <Box
      component="button"
      type="button"
      onClick={() => setPlaying((p) => !p)}
      sx={{
        cursor: "pointer",
        fontFamily: "inherit",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.875,
        py: 0.25,
        borderRadius: "999px",
        bgcolor: accent.greenSoft,
        border: `1px solid ${accent.greenBorder}`,
        color: accent.green,
        fontSize: 11.5,
        fontWeight: 700,
      }}
    >
      {playing ? <PauseIcon sx={{ fontSize: 14 }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
      Listen
      <Box component="span" sx={{ color: text.muted, fontWeight: 600 }}>
        {listenDuration(itemId)}
      </Box>
    </Box>
  );
}

function Receipt({ status }: { status: ReceiptStatus }) {
  const seen = status === "seen" || status === "heard";
  const heard = status === "heard";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: seen ? accent.green : text.muted }}>
        {seen ? <DoneAllIcon sx={{ fontSize: 14 }} /> : <DoneIcon sx={{ fontSize: 14 }} />}
        {seen ? "Seen" : "Sent"}
      </Box>
      {heard && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: accent.green }}>
          <HearingIcon sx={{ fontSize: 14 }} />
          Heard
        </Box>
      )}
    </Box>
  );
}

function MessageBubble({ message, receipt }: { message: FeedMessage; receipt?: ReceiptStatus }) {
  const inbound = message.direction === "inbound";
  return (
    <Box sx={{ display: "flex", justifyContent: inbound ? "flex-start" : "flex-end" }}>
      <Box
        sx={{
          maxWidth: "78%",
          display: "flex",
          flexDirection: "column",
          alignItems: inbound ? "flex-start" : "flex-end",
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: "12px",
            borderBottomLeftRadius: inbound ? "2px" : "12px",
            borderBottomRightRadius: inbound ? "12px" : "2px",
            bgcolor: inbound ? zinc[800] : accent.blueSoft,
            border: `1px solid ${inbound ? zinc[700] : accent.blueBorder}`,
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: text.primary, lineHeight: 1.4 }}>{message.preview}</Typography>
          <Box sx={{ mt: 0.75 }}>
            <ListenButton itemId={message.itemId} />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 0.375,
            fontSize: 11,
            color: text.muted,
          }}
        >
          <Box component="span">
            {inbound ? "Driver" : "Dispatch"} · {format(new Date(message.createdAt), "HH:mm")}
          </Box>
          {!inbound && receipt && (
            <>
              <Box component="span" sx={{ color: zinc[700] }}>
                ·
              </Box>
              <Receipt status={receipt} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** Seed receipts for the demo thread: dispatch messages already landed as heard. */
function seedReceipts(messages: FeedMessage[]): Record<string, ReceiptStatus> {
  const map: Record<string, ReceiptStatus> = {};
  for (const m of messages) {
    if (m.direction === "outbound") map[m.itemId] = "heard";
  }
  return map;
}

export function DriverMessageView({
  driverName,
  truck,
  messages,
  activity,
  onBack,
}: {
  driverName: string;
  truck: string;
  messages: FeedMessage[];
  activity: DriverActivityEvent[];
  onBack: () => void;
}) {
  const [thread, setThread] = useState<FeedMessage[]>(() => messages);
  const [receipts, setReceipts] = useState<Record<string, ReceiptStatus>>(() => seedReceipts(messages));
  const scrollRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  const sendMessage = useCallback((messageText: string) => {
    const itemId = `compose_${Date.now()}`;
    const reply: FeedMessage = {
      itemId,
      driverId: "",
      createdAt: new Date().toISOString(),
      preview: messageText,
      direction: "outbound",
      unread: false,
      isSilent: false,
      isFromBot: false,
      hasTranscription: false,
    };
    setThread((prev) => [...prev, reply]);
    setReceipts((prev) => ({ ...prev, [itemId]: "sent" }));
    // Simulate the driver's device acknowledging and then playing the message.
    timersRef.current.push(
      setTimeout(() => setReceipts((prev) => ({ ...prev, [itemId]: "seen" })), 1400),
      setTimeout(() => setReceipts((prev) => ({ ...prev, [itemId]: "heard" })), 3000),
    );
  }, []);

  const entries = buildTimeline(thread, activity);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to the newest entry whenever the thread grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [thread.length]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.75,
          borderBottom: `1px solid ${zinc[800]}`,
          flexShrink: 0,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onBack}
          sx={{
            cursor: "pointer",
            fontFamily: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 13,
            fontWeight: 600,
            color: text.secondary,
            bgcolor: "transparent",
            border: `1px solid ${zinc[700]}`,
            borderRadius: "8px",
            px: 1.25,
            py: 0.75,
            "&:hover": { color: text.primary, borderColor: zinc[600] },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: text.primary }}>{driverName}</Typography>
        <Chip label={`Truck ${truck}`} tone="neutral" />
        <Typography sx={{ fontSize: 13, color: text.muted }}>Message history & activity</Typography>
      </Box>

      {/* interleaved messages + activity, one chronological timeline */}
      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 2.5, maxWidth: 820, mx: "auto" }}>
          {entries.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: text.muted }}>No message history for this driver.</Typography>
          ) : (
            entries.map((entry) =>
              entry.kind === "message" ? (
                <MessageBubble key={entry.id} message={entry.message} receipt={receipts[entry.id]} />
              ) : (
                <ActivityEventMarker key={entry.id} event={entry.event} />
              ),
            )
          )}
        </Box>
      </Box>

      <MessageHistoryComposer driverName={driverName} onSend={sendMessage} />
    </Box>
  );
}
